/**
 * SlidePanel — slide-in overlay panel (right rail on desktop, bottom sheet on mobile).
 *
 * @description
 *   A panel that slides in from the right on desktop and up from the bottom on
 *   mobile. Desktop is a NON-MODAL rail: the background stays fully interactive
 *   (no backdrop). Mobile is a native-feeling bottom sheet with a backdrop and a
 *   minimize-to-handle feature that lets users browse the background while keeping
 *   form state. Handles its own keyboard + focus a11y: Escape closes **while focus is
 *   inside the panel** — the keydown
 *   listener is scoped to the panel node (NOT document), so a nested MUI Dialog/Menu
 *   (which portals to document.body, outside the panel subtree) closes alone on
 *   Escape without dismissing the panel. Focus moves into the panel on open and
 *   restores to the previously-focused element on close. No focus trap — the desktop
 *   rail is non-modal by design (role="region"), so trapping would mislead AT users;
 *   the mobile sheet has a backdrop and is modal FOR THE MOUSE — but nothing is
 *   `inert` and there is no trap, so it is NOT modal for the keyboard. It therefore
 *   carries `role="dialog"` WITHOUT `aria-modal`.
 *
 *   🔴 An earlier revision of this file DID carry `aria-modal="true"` here, and both
 *   gates called it out: it tells a screen reader the rest of the page is unreachable
 *   while it is still fully reachable by tab. A wrong promise to AT is worse than a
 *   missing one. If the sheet should ever really be modal, the answer is a trap plus
 *   `inert` on the background — not the attribute on its own.
 *
 *   🔴 **Die Einschraenkung ist neu und beabsichtigt.** Bis zum 2026-08-18 hing der
 *   Listener am FENSTER und schloss das Panel von ueberall auf der Seite, auch mit
 *   Fokus im Hintergrund — bei einer nicht-modalen Schiene durchaus sinnvoll. Der
 *   Knoten-Listener kann das nicht: er faengt nur, was durch den Teilbaum blubbert.
 *   Der Tausch loest dafuer ein groesseres Problem (verschachtelte MUI-Overlays
 *   schliessen allein, ohne DOM-Praesenz-Heuristik), aber er ist ein TAUSCH und
 *   keine reine Verbesserung. Vom Regel-15-Gate benannt, als es gefragt wurde, ob
 *   der Neubau etwas verloren hat.
 *
 * @useWhen
 *   - Detail / inspector side panel that should not block the page (desktop)
 *   - Compose or review panel the user may want to minimize and browse behind (mobile)
 *   - Drill-in from a table row where a full modal would be too heavy
 *
 * @dontUseFor
 *   - A blocking, must-answer modal — use MUI `Dialog` (true modal + focus trap)
 *   - A responsive form that is modal on both platforms — use `ActionSheet`
 *   - A drop-in Dialog that just needs bottom-sheet on mobile — use `MobileSheet`
 *
 * @related
 *   - Dialog (MUI) — use for blocking modals; SlidePanel is non-modal on desktop
 *   - ActionSheet — modal-on-both-platforms form; SlidePanel is non-modal rail
 *   - MobileSheet — Dialog-replacement with mobile sheet; no minimize handle
 *
 * @reuseHints
 *   - Any detail-drawer that should leave the list visible (PO, RMA, discovery, products)
 *   - Inline review surfaces where minimize-and-compare matters
 *
 * @props
 *   open       boolean      Whether the panel is open
 *   onClose    () => void   Called to fully close the panel (also fired on Escape / backdrop)
 *   title      string       Header title; also used as the panel's accessible name
 *   subtitle   string?      Optional subtitle / progress hint shown when minimized
 *   width      number       Desktop panel width in px (default 520)
 *   children   ReactNode
 */

import React, { useState, useEffect, useRef } from 'react'
import { Box, Stack, Typography, IconButton, useTheme, useMediaQuery } from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp'

// Shared timing — matches Material motion spec (standard easing)
const SLIDE_TRANSITION = 'transform 0.32s cubic-bezier(0.4, 0, 0.2, 1)'
const FADE_TRANSITION   = 'opacity 0.28s ease'

const HANDLE_H = 56   // px of header visible when minimized on mobile
const APP_BAR_H = 64  // desktop app bar height; panel sits below it

export default function SlidePanel({
  open,
  onClose,
  title,
  subtitle,
  width = 520,
  children,
}) {
  const theme   = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))

  // minimized is only meaningful on mobile
  const [minimized, setMinimized] = useState(false)

  // `mounted` controls DOM presence so the close animation can play
  const [mounted, setMounted] = useState(open)

  // a11y: the panel container (focus move-in target) + the element focused
  // before the panel opened (focus-restore target on close).
  const panelRef = useRef(null)
  const prevFocusRef = useRef(null)

  useEffect(() => {
    if (open) {
      setMounted(true)
      setMinimized(false)
    } else {
      const t = setTimeout(() => setMounted(false), 360)
      return () => clearTimeout(t)
    }
  }, [open])

  // a11y — Escape closes the panel. The listener is scoped to the PANEL NODE,
  // NOT document: a nested MUI <Dialog>/<Menu> portals to document.body (OUTSIDE
  // this panel's subtree), so its Escape keydown never bubbles here and the inner
  // overlay closes alone. (A document-level listener + a `defaultPrevented` check
  // does NOT work — MUI v7 Modal only stopPropagation()s a React-synthetic
  // keydown on Escape and never preventDefault()s, so a native document listener
  // can't detect that a nested modal consumed it. Scoping to the node is the fix.)
  // `mounted` is in the deps because the node only exists after the mount render,
  // so the effect must re-run once panelRef.current is attached.
  useEffect(() => {
    if (!open) return undefined
    const node = panelRef.current
    if (!node) return undefined
    const onKeyDown = (e) => {
      if (e.key !== 'Escape') return
      e.stopPropagation()
      onClose?.()
    }
    node.addEventListener('keydown', onKeyDown)
    return () => node.removeEventListener('keydown', onKeyDown)
  }, [open, onClose, mounted])

  // a11y — move focus into the panel on open; restore it on close.
  // Restore guards against the 360ms-unmount: the previously-focused node may
  // be gone (detached) by the time we close, so only refocus if still in the DOM.
  useEffect(() => {
    if (open) {
      prevFocusRef.current = document.activeElement
      // defer to after the panel paints so the focusable target exists
      const t = setTimeout(() => {
        const first = panelRef.current?.querySelector(
          'button,[href],input,select,textarea,[tabindex]:not([tabindex="-1"])'
        )
        first?.focus()
      }, 0)
      return () => clearTimeout(t)
    }
    const prev = prevFocusRef.current
    if (prev && document.contains(prev)) prev.focus()
    return undefined
  }, [open])

  if (!mounted) return null

  // ─── Desktop ────────────────────────────────────────────────────────────────
  if (!isMobile) {
    return (
      <Box
        ref={panelRef}
        role="region"
        aria-label={title}
        sx={{
          position: 'fixed',
          top:    APP_BAR_H,
          right:  0,
          bottom: 0,
          width,
          zIndex: 1250,
          display: 'flex',
          flexDirection: 'column',
          bgcolor: 'background.paper',
          borderLeft: '1px solid',
          borderColor: 'divider',
          boxShadow: '-4px 0 24px rgba(15,23,42,0.10)',
          // Slide animation
          transform:  open ? 'translateX(0)' : 'translateX(100%)',
          transition: SLIDE_TRANSITION,
        }}
      >
        {/* Panel header */}
        <Stack
          direction="row"
          alignItems="center"
          sx={{
            px: 2.5, py: 1.75,
            borderBottom: '1px solid', borderColor: 'divider',
            flexShrink: 0,
          }}
        >
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="subtitle1" fontWeight={700} noWrap>{title}</Typography>
            {subtitle && (
              <Typography variant="caption" color="text.secondary" display="block" noWrap>
                {subtitle}
              </Typography>
            )}
          </Box>
          <IconButton size="small" onClick={onClose} aria-label="Close panel" sx={{ ml: 1 }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Stack>

        {/* Scrollable content */}
        <Box sx={{ flex: 1, overflowY: 'auto' }}>
          {children}
        </Box>
      </Box>
    )
  }

  // ─── Mobile bottom sheet ─────────────────────────────────────────────────────
  const translateY = !open      ? '100%'
    : minimized               ? `calc(100% - ${HANDLE_H}px)`
    :                            '0%'

  return (
    <>
      {/* Backdrop — visible only when fully open (not minimized).
          When minimized the backdrop fades away so the user can browse freely. */}
      <Box
        onClick={() => { if (!minimized) onClose() }}
        sx={{
          position: 'fixed', inset: 0,
          zIndex: 1249,
          bgcolor: 'rgba(0,0,0,0.45)',
          opacity: open && !minimized ? 1 : 0,
          pointerEvents: open && !minimized ? 'auto' : 'none',
          transition: FADE_TRANSITION,
        }}
      />

      {/* Bottom sheet — effectively modal (has backdrop when fully open) */}
      <Box
        ref={panelRef}
        role="dialog"
        aria-label={title}
        sx={{
          position:        'fixed',
          left: 0, right: 0, bottom: 0,
          height:          '90dvh',
          zIndex:          1250,
          display:         'flex',
          flexDirection:   'column',
          bgcolor:         'background.paper',
          borderRadius:    '16px 16px 0 0',
          boxShadow:       '0 -6px 32px rgba(0,0,0,0.18)',
          overflow:        'hidden',
          transform:       `translateY(${translateY})`,
          transition:      SLIDE_TRANSITION,
        }}
      >
        {/* Drag-handle indicator */}
        <Box sx={{ pt: 1.25, pb: 0.5, display: 'flex', justifyContent: 'center', flexShrink: 0 }}>
          <Box sx={{ width: 36, height: 4, borderRadius: 2, bgcolor: 'action.disabled' }} />
        </Box>

        {/* Sheet header — tapping it when minimized restores the sheet */}
        <Stack
          direction="row"
          alignItems="center"
          onClick={minimized ? () => setMinimized(false) : undefined}
          sx={{
            px: 2, pb: 1.5,
            flexShrink: 0,
            cursor: minimized ? 'pointer' : 'default',
          }}
        >
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="subtitle1" fontWeight={700} noWrap>{title}</Typography>
            {subtitle && (
              <Typography variant="caption" color="text.secondary" display="block" noWrap>
                {subtitle}
              </Typography>
            )}
          </Box>
          <Stack direction="row" spacing={0.25}>
            <IconButton
              size="small"
              onClick={e => { e.stopPropagation(); setMinimized(m => !m) }}
              aria-label={minimized ? 'Restore' : 'Minimize'}
            >
              {minimized
                ? <KeyboardArrowUpIcon fontSize="small" />
                : <KeyboardArrowDownIcon fontSize="small" />
              }
            </IconButton>
            <IconButton size="small" onClick={onClose} aria-label="Close">
              <CloseIcon fontSize="small" />
            </IconButton>
          </Stack>
        </Stack>

        {/* Content — always mounted (React state survives minimize), hidden via display */}
        <Box sx={{ flex: 1, overflowY: 'auto', display: minimized ? 'none' : 'block' }}>
          {children}
        </Box>
      </Box>
    </>
  )
}
